export interface INavigationRoute {
  name: string
  displayName: string
  /** Module slug(s) for parent sections, or permission `name` for leaf items. Parents may list multiple slugs (OR). */
  gate?: string | string[] | undefined
  meta: { icon: string }
  children?: INavigationRoute[]
  hideInSidebar?: boolean
  /** Extra route names that should highlight this item or keep the parent section expanded. */
  activeRoutes?: string[]
  /**
   * Inventory configuration key that must be enabled for this item to appear
   * (e.g. `enable_transfers`). Underscore and hyphen forms are both accepted.
   */
  requiresInventoryConfig?: string
}

import { catalogProductsNavItem, catalogUnitsNavItem, inventorySidebarChildren } from '../../modules/inventory/menu'
import { servicesSetupMenuChildren } from '../../modules/services/menu'
import { procurementMainMenuChildren } from '../../modules/procurement/menu'
import { expenseFinanceMenuChildren } from '../../modules/expense/menu'
import { commissionFinanceMenuChildren } from '../../modules/commission/menu'

export default {
  root: {
    name: '/',
    displayName: 'navigationRoutes.home',
  },
  routes: [
    {
      name: 'dashboard',
      displayName: 'menu.dashboard',
      gate: 'dashboard',
      meta: {
        icon: 'vuestic-iconset-dashboard',
      },
    },
    {
      name: 'clients',
      displayName: 'menu.clients',
      gate: 'clients',
      meta: {
        icon: 'group',
      },
    },
    {
      name: 'sales',
      displayName: 'menu.sales',
      gate: 'sales',
      meta: {
        icon: 'credit_card',
      },
      children: [
        {
          name: 'sales-list',
          displayName: 'menu.salesOrders',
          gate: 'sale',
          meta: {
            icon: 'list_alt',
          },
          activeRoutes: ['sales-list', 'new-sale', 'sale-details'],
        },
        {
          name: 'invoice-search',
          displayName: 'menu.invoices',
          gate: 'invoice',
          meta: {
            icon: 'receipt_long',
          },
        },
      ],
    },
    {
      name: 'finance',
      displayName: 'menu.finance',
      gate: 'finance',
      meta: {
        icon: 'account_balance',
      },
      children: [
        {
          name: 'finance-index',
          displayName: 'menu.financeOverview',
          gate: 'finance',
          meta: {
            icon: 'dashboard',
          },
        },
        {
          name: 'make-payment',
          displayName: 'menu.financePayments',
          gate: 'payment',
          meta: {
            icon: 'payments',
          },
          activeRoutes: ['make-payment', 'payments', 'payment-details', 'invoice-payment'],
        },
        ...expenseFinanceMenuChildren,
        ...commissionFinanceMenuChildren,
        {
          name: 'payment-history',
          displayName: 'menu.transactions',
          gate: 'transaction',
          meta: {
            icon: 'history',
          },
        },
      ],
    },
    {
      name: 'inventory',
      displayName: 'menu.inventory',
      gate: 'inventory',
      meta: {
        icon: 'inventory_2',
      },
      children: [
        ...inventorySidebarChildren,
        {
          name: 'inventory-stock-in',
          displayName: 'menu.stockIn',
          gate: 'stock-in',
          meta: {
            icon: 'input',
          },
        },
        {
          name: 'inventory-stock-out',
          displayName: 'menu.stockOut',
          gate: 'stock-out',
          meta: {
            icon: 'tune',
          },
        },
        {
          name: 'inventory-transfers',
          displayName: 'menu.transfers',
          gate: 'stock-transfer',
          requiresInventoryConfig: 'enable_transfers',
          meta: {
            icon: 'swap_horiz',
          },
          activeRoutes: ['inventory-transfer-create', 'inventory-transfer-detail', 'inventory-transfer-receive'],
        },
        {
          name: 'inventory-stock-takes',
          displayName: 'menu.stockTake',
          gate: 'stock-take',
          requiresInventoryConfig: 'enable_stock_counts',
          meta: {
            icon: 'clipboard_check',
          },
          activeRoutes: ['inventory-stock-takes', 'inventory-stock-take-register', 'inventory-stock-take-review'],
        },
      ],
    },
    {
      name: 'procurement',
      displayName: 'menu.procurement',
      gate: 'procurement',
      meta: {
        icon: 'shopping_cart',
      },
      children: [...procurementMainMenuChildren],
    },
    {
      name: 'users',
      displayName: 'menu.usersAndRoles',
      gate: 'users',
      meta: {
        icon: 'group',
      },
      children: [
        {
          name: 'active-users',
          displayName: 'menu.systemUsers',
          gate: 'active-user',
          meta: {
            icon: 'person',
          },
        },
        {
          name: 'staff',
          displayName: 'menu.staff',
          gate: 'staff',
          meta: {
            icon: 'badge',
          },
        },
        {
          name: 'invitations',
          displayName: 'menu.invitations',
          gate: 'user-invitation',
          meta: {
            icon: 'mail',
          },
        },
        {
          name: 'roles',
          displayName: 'menu.rolesAndPermissions',
          gate: 'role-permission',
          meta: {
            icon: 'admin_panel_settings',
          },
        },
      ],
    },
    {
      name: 'catalog',
      displayName: 'menu.catalog',
      gate: 'catalog',
      meta: {
        icon: 'category',
      },
      children: [catalogProductsNavItem, catalogUnitsNavItem, ...servicesSetupMenuChildren],
    },
    {
      name: 'reports',
      displayName: 'menu.reports',
      gate: 'reports',
      meta: {
        icon: 'bar_chart',
      },
      children: [
        {
          name: 'financial-reports',
          displayName: 'menu.financeReports',
          gate: 'report-finance',
          meta: {
            icon: 'assessment',
          },
        },
        {
          name: 'items-reports',
          displayName: 'menu.itemsReports',
          gate: 'report-inventory',
          meta: {
            icon: 'inventory_2',
          },
        },
      ],
    },
    {
      name: 'settings-hub',
      displayName: 'menu.settingsApplication',
      gate: 'settings',
      meta: {
        icon: 'settings',
      },
      children: [
        {
          name: 'settings',
          displayName: 'menu.companySettings',
          gate: 'company-settings',
          meta: {
            icon: 'business',
          },
        },
        {
          name: 'configurations',
          displayName: 'menu.configurations',
          gate: 'configuration',
          meta: {
            icon: 'tune',
          },
        },
        {
          name: 'preferences',
          displayName: 'menu.preferences',
          meta: {
            icon: 'manage_accounts',
          },
        },
        {
          name: 'data-imports',
          displayName: 'menu.dataImports',
          gate: 'data-imports',
          meta: {
            icon: 'upload_file',
          },
        },
      ],
    },
  ] as INavigationRoute[],
}
